import { useEffect, useState } from 'react'
import type { Frame } from '@praesto/shared'
import { useText } from '../i18n.js'
import type { Notes } from './useNotes.js'

/** Verstrichene Zeit als `mm:ss`, ab einer Stunde als `h:mm:ss`. */
function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(s / 60)
  const rest = String(s % 60).padStart(2, '0')
  if (minutes < 60) return `${minutes}:${rest}`
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${rest}`
}

/**
 * Was der Vortragende sieht und das Publikum nicht: Notiz zum laufenden Frame,
 * der nächste Frame und die verstrichene Zeit.
 *
 * Die Uhr läuft mit dem Vortrag, nicht mit dem Frame: Sie beantwortet „wie lange
 * rede ich schon", und das ist die Frage, die man sich während eines Vortrags
 * tatsächlich stellt.
 */
export function NotesPanel({
  notes,
  frames,
  frameIndex,
}: {
  notes: Notes
  frames: Frame[]
  frameIndex: number
}): React.ReactElement {
  const t = useText()
  const current = frames[frameIndex]
  const next = frames[frameIndex + 1]
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const started = performance.now()
    const timer = setInterval(() => setElapsed((performance.now() - started) / 1000), 1000)
    return () => clearInterval(timer)
  }, [])

  const text = current ? notes.get(current.id) : ''

  return (
    <aside className="notes-panel">
      <header className="notes-head">
        <span>{t('notes.title')}</span>
        <span className="notes-clock" title={t('notes.clockTitle')}>
          {formatElapsed(elapsed)}
        </span>
      </header>

      {text.trim() ? (
        <p className="notes-text">{text}</p>
      ) : (
        <p className="notes-empty">{t('notes.empty')}</p>
      )}

      <footer className="notes-next">
        {next ? (
          <>
            <span className="muted small">{t('notes.next')}</span> {next.name}
          </>
        ) : (
          <span className="muted small">{t('notes.last')}</span>
        )}
      </footer>
    </aside>
  )
}
